import { useState, useEffect, useCallback } from 'react';
import { 
  getTemplates, 
  createTemplate as createTemplateFirestore, 
  updateTemplate as updateTemplateFirestore,
  getTemplate as getTemplateFirestore,
  deleteTemplate as deleteTemplateFirestore
} from '../../firebase/unifiedDataService';
import { toExportFormatArray } from '../../utils/questionSchemaConverter';
import { Logger } from '../../utils/logger';

const log = Logger.create('useQuestionPersistence');

/**
 * Custom Hook für Firestore Persistenz von Fragen
 * 
 * Verwaltet:
 * - Laden und Speichern von Quiz-Templates (Firestore)
 * - Session-Liste
 * - Download als JSON
 * 
 * WICHTIG: Dieser Hook verwendet jetzt Firestore statt Realtime Database,
 * damit Vorlagen im Dashboard angezeigt werden.
 */
export function useQuestionPersistence(userId) {
  const [existingSessions, setExistingSessions] = useState([]);
  const [selectedSession, setSelectedSession] = useState(null);
  const [isSaving, setIsSaving] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [saveError, setSaveError] = useState(null);
  
  /**
   * Fetch existing templates from Firestore
   */
  const fetchSessions = useCallback(async () => {
    if (!userId) return;
    
    setIsLoading(true);
    try {
      const templates = await getTemplates(userId);
      
      const templatesArray = templates.map(template => ({
        id: template.id,
        name: template.name || template.id,
        questionCount: template.questions?.length || 0,
        createdAt: template.createdAt,
        updatedAt: template.updatedAt || template.lastModified,
        courseId: template.courseId
      }));
      
      // Sort by most recently updated
      templatesArray.sort((a, b) => (b.updatedAt || b.createdAt || 0) - (a.updatedAt || a.createdAt || 0));
      setExistingSessions(templatesArray);
    } catch (error) {
      log.error('Error fetching templates:', error);
      setSaveError('Fehler beim Laden der Vorlagen');
      setExistingSessions([]);
    } finally {
      setIsLoading(false);
    }
  }, [userId]);
  
  // Fetch sessions on mount and when userId changes
  useEffect(() => {
    if (userId) {
      fetchSessions();
    }
  }, [userId, fetchSessions]);
  
  /**
   * Save questions to a session (Firestore)
   * 
   * @param {string} sessionId - Existing session ID (null for new)
   * @param {Array} questions - Questions to save
   * @param {string} newName - Name for new template (required if sessionId is null)
   * @returns {Promise<{success: boolean, sessionId?: string, error?: string}>}
   */
  const saveToSession = useCallback(async (sessionId, questions, newName = null) => {
    if (!userId) {
      return { success: false, error: 'Du musst eingeloggt sein um zu speichern' };
    }
    
    if (!questions || questions.length === 0) {
      return { success: false, error: 'Keine Fragen zum Speichern vorhanden' };
    }
    
    setIsSaving(true);
    setSaveError(null);
    
    try {
      const exportData = toExportFormatArray(questions);
      
      if (newName) {
        // Create NEW template in Firestore
        const newId = await createTemplateFirestore({
          name: newName,
          userId: userId,
          owner: userId,
          courseId: null, // Neue Vorlagen ohne Kurs
          description: '',
          tags: [],
          questions: exportData,
          settings: {
            displayName: newName,
            showIntroScreen: true,
            anonymousMode: false,
            enableGamification: true
          }
        });
        
        // Refresh sessions list
        await fetchSessions();
        setSelectedSession(newId);
        
        setIsSaving(false);
        return {
          success: true,
          sessionId: newId,
          message: `Neue Vorlage "${newName}" mit ${exportData.length} Fragen erstellt!`
        };
        
      } else if (sessionId) {
        // Add to EXISTING template in Firestore
        const existingTemplate = await getTemplateFirestore(sessionId);
        
        if (!existingTemplate) {
          setIsSaving(false);
          return { success: false, error: 'Vorlage nicht gefunden' };
        }
        
        const existingQuestions = existingTemplate.questions || [];
        const mergedQuestions = [...existingQuestions, ...exportData];
        
        await updateTemplateFirestore(sessionId, {
          questions: mergedQuestions
        });
        
        const templateName = existingTemplate.name || sessionId;
        
        // Refresh sessions list
        await fetchSessions();
        
        setIsSaving(false);
        return {
          success: true,
          sessionId,
          message: `${exportData.length} Fragen zu "${templateName}" hinzugefügt (jetzt ${mergedQuestions.length} Fragen)`
        };
        
      } else {
        setIsSaving(false);
        return { success: false, error: 'Bitte wähle eine Vorlage aus oder erstelle eine neue' };
      }
      
    } catch (err) {
      log.error('Save error:', err);
      setSaveError(err.message);
      setIsSaving(false);
      return { success: false, error: `Fehler beim Speichern: ${err.message}` };
    }
  }, [userId, fetchSessions]);
  
  /**
   * Replace all questions in a template (Firestore)
   */
  const replaceTemplateQuestions = useCallback(async (sessionId, questions) => {
    if (!userId || !sessionId) {
      return { success: false, error: 'Ungültige Parameter' };
    }
    
    setIsSaving(true);
    setSaveError(null);
    
    try {
      const exportData = toExportFormatArray(questions);
      
      await updateTemplateFirestore(sessionId, {
        questions: exportData
      });
      
      await fetchSessions();
      
      setIsSaving(false);
      return {
        success: true,
        message: `Vorlage mit ${exportData.length} Fragen aktualisiert`
      };
      
    } catch (err) {
      setSaveError(err.message);
      setIsSaving(false);
      return { success: false, error: err.message };
    }
  }, [userId, fetchSessions]);
  
  /**
   * Download questions as JSON file
   */
  const downloadQuestions = useCallback((questions, filename = 'generated-questions.json') => {
    const exportData = toExportFormatArray(questions);
    const blob = new Blob([JSON.stringify(exportData, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  }, []);
  
  /**
   * Load questions from a template (Firestore)
   */
  const loadFromSession = useCallback(async (sessionId) => {
    if (!userId || !sessionId) return { success: false, questions: [] };
    
    setIsLoading(true);
    try {
      const template = await getTemplateFirestore(sessionId);
      
      if (template) {
        setIsLoading(false);
        return {
          success: true,
          questions: template.questions || [],
          templateName: template.name
        };
      }
      
      setIsLoading(false);
      return { success: false, questions: [], error: 'Vorlage nicht gefunden' };
      
    } catch (err) {
      setIsLoading(false);
      return { success: false, questions: [], error: err.message };
    }
  }, [userId]);
  
  /**
   * Delete a template (Firestore)
   */
  const deleteTemplate = useCallback(async (sessionId) => {
    if (!userId || !sessionId) return { success: false };
    
    try {
      await deleteTemplateFirestore(sessionId);
      
      await fetchSessions();
      
      if (selectedSession === sessionId) {
        setSelectedSession(null);
      }
      
      return { success: true };
      
    } catch (err) {
      return { success: false, error: err.message };
    }
  }, [userId, selectedSession, fetchSessions]);
  
  return {
    // State
    existingSessions,
    selectedSession,
    isSaving,
    isLoading,
    saveError,
    
    // Setters
    setSelectedSession,
    
    // Actions
    fetchSessions,
    saveToSession,
    replaceTemplateQuestions,
    loadFromSession,
    downloadQuestions,
    deleteTemplate,
    
    // Utilities
    hasTemplates: existingSessions.length > 0,
    templateCount: existingSessions.length,
    getTemplateById: (id) => existingSessions.find(s => s.id === id)
  };
}

export default useQuestionPersistence;
